import React, { FC } from "react";
import styled from "styled-components";
import { Entry } from "contentful";
import { text } from "../lib/sharedStyles";
import GlobeIcon from "../components/Icons/Globe";
import InstagramIcon from "../components/Icons/Instagram";
import TwitterIcon from "../components/Icons/Twitter";
import FacebookIcon from "../components/Icons/Facebook";

interface SocialLinksProps extends Entry {
  id: string;
  fields: {
    title: string;
    theme?: string;
    artistWebsiteUrl?: string;
    artistInstagramUrl?: string;
    artistTwitterUrl?: string;
    artistFacebookUrl?: string;
  };
}

const SocialLinkContainer = styled.div<{ theme?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background-color: ${({ theme }) => (theme === "dark" ? "#000" : "#fff")};
  color: ${({ theme }) => (theme === "dark" ? "#fff" : "#000")};
  padding: 50px;
`;

const Title = styled.h4<{ theme?: string }>`
  margin-bottom: 10px;
  ${text(600, '24px', '24px', 'inherit')}
  letter-spacing: .125rem;
`;

const IconsWrapper = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  margin-top: 50px;
`;

const IconLink = styled.a<{ color?: string }>`
  color: #aaa;
  font-size: 24px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: color 0.3s ease;

  &:hover {
    color: #f1f1f1;
  }
`;

const SocialLinks: FC<SocialLinksProps> = ({
  fields: {
    title,
    theme,
    artistWebsiteUrl,
    artistInstagramUrl,
    artistTwitterUrl,
    artistFacebookUrl,
  },
}) => {
  const icons = [
    { Component: GlobeIcon, url: artistWebsiteUrl, label: "Artist Website" },
    { Component: InstagramIcon, url: artistInstagramUrl, label: "Instagram" },
    { Component: TwitterIcon, url: artistTwitterUrl, label: "Twitter" },
    { Component: FacebookIcon, url: artistFacebookUrl, label: "Facebook" },
  ];

  const renderIcon = (icon: {
    Component: FC<{ fontSize?: string; fill?: string }>;
    url?: string;
    label: string;
  }) => {
    const { Component, url, label } = icon;
    return url ? (
      <IconLink href={url} aria-label={label} target="_blank" rel="noopener noreferrer">
        <Component fontSize="24px" />
      </IconLink>
    ) : null;
  };

  return (
    <SocialLinkContainer theme={theme}>
      {title && <Title>{title}</Title>}
      <IconsWrapper>
        {icons.map((icon, index) => renderIcon(icon))}
      </IconsWrapper>
    </SocialLinkContainer>
  );
};

export default SocialLinks;